import {
  TASK_STATUSES,
  canBeProject,
  canHaveParent,
  normalizeTag,
  orderKeyBetween,
  parseQuickInput,
  repeatChange,
  shiftSprint,
  wouldCycle,
} from '@nedelnik/shared';

import { createLocalReopenMemory } from './reopen.js';

import type { ReopenMemory } from './reopen.js';
import type { SyncEngine } from './sync/engine.js';
import type {
  ChecklistItem,
  Repeat,
  SprintKey,
  SprintType,
  Task,
  TaskLink,
  TaskStatus,
  TaskTag,
} from '@nedelnik/shared';

/**
 * Правки задач, выраженные мутациями.
 *
 * Прослойка между интерфейсом и движком: компоненты знают «перевести в работу»
 * и «перенести в следующий спринт», но ничего не знают ни про мутации, ни про
 * очередь (слой 4 раздела 10 архитектуры). Здесь же живут решения, которые
 * обязаны совпадать во всех местах интерфейса, — например, что `done_at`
 * меняется вместе со статусом, а не отдельной кнопкой.
 */

export type CreateOutcome = 'created' | 'empty' | 'no-number';

export interface TaskActions {
  /**
   * Быстрая запись: разбирает `#тег` прямо в тексте и заводит задачу в
   * показанном спринте со статусом «бэклог» (3.3.3, 3.3.5).
   */
  create(
    raw: string,
    sprint: SprintKey | null,
    afterOrderKey: string | null,
  ): Promise<CreateOutcome>;
  setStatus(task: Task, status: TaskStatus): Promise<void>;
  /**
   * Снять отметку «готово»: задача возвращается в статус, из которого её
   * закрыли, — `to`, если его знает экран, иначе тот, что помнит устройство,
   * иначе бэклог (3.1.9).
   */
  reopen(task: Task, to?: TaskStatus): Promise<void>;
  /** Степпер статуса: обязательная альтернатива перетаскиванию (3.1.4). */
  shiftStatus(task: Task, direction: -1 | 1): Promise<void>;
  /** Итог перетаскивания: колонка и место в ней. */
  place(task: Task, status: TaskStatus, orderKey: string): Promise<void>;
  /** Перенос в соседний спринт в обе стороны, статус сохраняется (3.11.1, 3.11.2). */
  shiftTaskSprint(task: Task, direction: -1 | 1, fallback: SprintKey): Promise<void>;
  /** Массовый перенос незакрытых в целевой спринт (3.11.4). */
  carryOver(tasks: readonly Task[], target: SprintKey): Promise<void>;

  // --- панель задачи (3.5) ---

  /** Заголовок правится на месте; пустым он остаться не может (3.5). */
  setTitle(task: Task, title: string): Promise<void>;
  setNotes(task: Task, notes: string): Promise<void>;
  /**
   * Срок или его снятие: задача без срока — нормальный случай (3.6.1). Время
   * не обязательно и лишь уточняет дату (3.6.2); без даты оно не хранится.
   */
  setDue(task: Task, due: string | null, dueTime: string | null): Promise<void>;
  /** Спринт напрямую, включая возврат во Входящие (`null`). */
  setSprint(task: Task, sprint: SprintKey | null): Promise<void>;
  /** Повторять каждую неделю, каждый месяц или перестать (3.19). */
  setRepeat(task: Task, repeat: Repeat | null): Promise<void>;
  /** Удаление — надгробие, а не забвение (раздел 3.4 архитектуры). */
  remove(task: Task): Promise<void>;
  /** Вернуть задачу из корзины, пока надгробию не вышел срок (3.18.6). */
  restore(task: Task): Promise<void>;
  /** Стереть задачу из корзины, не дожидаясь конца срока. Необратимо (3.18.6). */
  purge(task: Task): Promise<void>;

  // --- настройки (3.13, 3.15) ---

  /**
   * Длина спринта: неделя или месяц (3.2.1).
   *
   * Единственная настройка, которая синхронизируется: спринт — это про сами
   * задачи, и на телефоне он обязан быть тем же, что на ноутбуке. Тема,
   * наоборот, живёт в устройстве.
   */
  setSprintType(userId: string, type: SprintType): Promise<void>;

  // --- проекты (3.8) ---

  /**
   * Вложить задачу в проект или вынуть её оттуда.
   *
   * Оба правила — «сама в себя нельзя» (3.8.3) и «уровней ровно два» — живут
   * здесь, а не только в подборщике: интерфейс их не показывает, но держаться
   * они обязаны и когда вызов пришёл откуда-то ещё.
   */
  setParent(task: Task, parentId: string | null, tasks: readonly Task[]): Promise<ParentOutcome>;

  /**
   * Завести проект прямо из карточки и сразу вложить в него задачу.
   *
   * Без этого зонтик заводится задом наперёд: закрыть карточку, записать
   * «переезд» строкой быстрой записи, открыть карточку заново и только тогда
   * выбрать. Возвращает идентификатор нового проекта или причину отказа.
   */
  createProject(
    title: string,
    sprint: SprintKey | null,
    afterOrderKey: string | null,
    /** Куда вложить новую задачу сразу: так заводится подзадача (3.8.10). */
    parentId?: string | null,
  ): Promise<{ outcome: CreateOutcome; id?: string }>;

  // --- связи (3.9) ---

  /**
   * Завести связь. `known` — **все** записи связей, включая снятые: пара
   * уникальна в базе вместе с надгробиями, поэтому снятую связь возвращают
   * воскрешением её же записи, а не новой.
   */
  addLink(blockerId: string, blockedId: string, known: readonly TaskLink[]): Promise<LinkOutcome>;
  removeLink(link: TaskLink): Promise<void>;

  // --- теги (3.5) ---

  /**
   * Навесить тег. `known` — все записи тегов задачи, **включая снятые**: пара
   * «задача и тег» уникальна в базе вместе с надгробиями, поэтому снятый тег
   * возвращают воскрешением его же записи, а не новой. То же самое и по той же
   * причине сделано у связей.
   */
  addTag(taskId: string, raw: string, known: readonly TaskTag[]): Promise<TagOutcome>;
  removeTag(tag: TaskTag): Promise<void>;

  // --- чек-лист (3.7) ---

  addChecklistItem(taskId: string, text: string, afterOrderKey: string | null): Promise<boolean>;
  setChecklistText(item: ChecklistItem, text: string): Promise<void>;
  toggleChecklistItem(item: ChecklistItem): Promise<void>;
  removeChecklistItem(item: ChecklistItem): Promise<void>;
}

export type TagOutcome = 'added' | 'empty' | 'duplicate';
/**
 * `nested-parent` — выбранный проект сам вложен, `is-project` — вкладываемая
 * задача сама проект. И то и другое дало бы третий уровень (см. `canBeProject`
 * в `shared`).
 */
export type ParentOutcome = 'set' | 'same' | 'cycle' | 'nested-parent' | 'is-project';
export type LinkOutcome = 'added' | 'same-task' | 'duplicate';

export function createTaskActions(
  engine: SyncEngine,
  now: () => Date = () => new Date(),
  reopenMemory: ReopenMemory = createLocalReopenMemory(),
): TaskActions {
  /**
   * Прежний статус запоминается здесь, а не у галочки: закрывают и галочкой, и
   * статусом в карточке, и броском в «Готово», и снятая потом галочка обязана
   * вернуть задачу туда, откуда её закрыли любым из этих способов.
   */
  function noteStatusChange(task: Task, status: TaskStatus): void {
    if (status === 'done' && task.status !== 'done') reopenMemory.remember(task.id, task.status);
    else if (status !== 'done' && task.status === 'done') reopenMemory.forget(task.id);
  }

  /**
   * Поля, которые меняются вместе со статусом. `done_at` — не отдельная
   * сущность, а отметка о закрытии: разъехавшись со статусом, она врёт про
   * дату, к которой задача принадлежит.
   */
  function statusFields(status: TaskStatus): Record<string, unknown> {
    const stamp = now().toISOString();
    return {
      status,
      done_at: status === 'done' ? stamp : null,
      updated_at: stamp,
    };
  }

  async function createTask(
    raw: string,
    sprint: SprintKey | null,
    afterOrderKey: string | null,
    parentId: string | null = null,
  ): Promise<{ outcome: CreateOutcome; id?: string }> {
    const parsed = parseQuickInput(raw);
    if (parsed.title.length === 0) return { outcome: 'empty' };

    // Номер задачи обязателен, а блоки номеров выдаёт сервер. Завести задачу
    // без номера нельзя: сервер её отвергнет, и правка молча уедет в карантин.
    const number = await engine.allocateNumber();
    if (number === undefined) return { outcome: 'no-number' };

    const id = crypto.randomUUID();
    const stamp = now().toISOString();

    await engine.mutate({
      entity: 'task',
      entityId: id,
      kind: 'upsert',
      fields: {
        number,
        title: parsed.title,
        notes: null,
        status: 'backlog',
        sprint,
        due: null,
        due_time: null,
        repeat: null,
        series_id: null,
        parent_id: parentId,
        order_key: orderKeyBetween(afterOrderKey, null),
        created_at: stamp,
        updated_at: stamp,
        done_at: null,
        created_by: 'human',
      },
    });

    // Тег — отдельная запись, а не элемент массива внутри задачи: при LWW по
    // полю два тега, добавленных с двух устройств, схлопнулись бы в один
    // (раздел 3.3 архитектуры).
    for (const tag of parsed.tags) {
      await engine.mutate({
        entity: 'task_tag',
        entityId: crypto.randomUUID(),
        kind: 'upsert',
        fields: { task_id: id, tag, created_at: stamp },
      });
    }

    return { outcome: 'created', id };
  }

  async function create(
    raw: string,
    sprint: SprintKey | null,
    afterOrderKey: string | null,
  ): Promise<CreateOutcome> {
    return (await createTask(raw, sprint, afterOrderKey)).outcome;
  }

  async function setStatus(task: Task, status: TaskStatus): Promise<void> {
    if (task.status === status) return;
    noteStatusChange(task, status);
    await engine.mutate({
      entity: 'task',
      entityId: task.id,
      kind: 'upsert',
      fields: statusFields(status),
    });
  }

  async function reopen(task: Task, to?: TaskStatus): Promise<void> {
    if (task.status !== 'done') return;
    const target = to ?? reopenMemory.recall(task.id) ?? 'backlog';
    await setStatus(task, target === 'done' ? 'backlog' : target);
  }

  async function shiftStatus(task: Task, direction: -1 | 1): Promise<void> {
    const index = TASK_STATUSES.indexOf(task.status) + direction;
    const next = TASK_STATUSES[index];
    // Края молчат, а не заворачивают на противоположный конец: «следующий»
    // после «готово» — это не «бэклог», и обещать иное степпер не должен.
    if (next === undefined) return;
    await setStatus(task, next);
  }

  async function place(task: Task, status: TaskStatus, orderKey: string): Promise<void> {
    noteStatusChange(task, status);
    await engine.mutate({
      entity: 'task',
      entityId: task.id,
      kind: 'upsert',
      fields: {
        ...(task.status === status ? { updated_at: now().toISOString() } : statusFields(status)),
        order_key: orderKey,
      },
    });
  }

  async function moveToSprint(task: Task, sprint: SprintKey | null): Promise<void> {
    if (task.sprint === sprint) return;
    // Статус не трогаем намеренно: взятое в работу остаётся в работе (3.11.2).
    await engine.mutate({
      entity: 'task',
      entityId: task.id,
      kind: 'upsert',
      fields: { sprint, updated_at: now().toISOString() },
    });
  }

  async function shiftTaskSprint(
    task: Task,
    direction: -1 | 1,
    fallback: SprintKey,
  ): Promise<void> {
    // У задачи вне спринтов сдвигать нечего: она попадает в показанный спринт,
    // и это тот же способ отправки, что и перенос между спринтами (3.4.3).
    const target = task.sprint === null ? fallback : shiftSprint(task.sprint, direction);
    await moveToSprint(task, target);
  }

  async function carryOver(tasks: readonly Task[], target: SprintKey): Promise<void> {
    for (const task of tasks) {
      await moveToSprint(task, target);
    }
  }

  // ------------------------------------------------------------------------
  // Панель задачи
  // ------------------------------------------------------------------------

  /** Одно поле задачи. `updated_at` едет вместе с ним — иначе он врёт. */
  async function patch(task: Task, fields: Record<string, unknown>): Promise<void> {
    await engine.mutate({
      entity: 'task',
      entityId: task.id,
      kind: 'upsert',
      fields: { ...fields, updated_at: now().toISOString() },
    });
  }

  async function setTitle(task: Task, title: string): Promise<void> {
    const value = title.replace(/\s+/g, ' ').trim();
    // Задача без заголовка неотличима от пустой строки в списке: её не открыть
    // и не найти. Пустую правку не применяем, поле вернётся к прежнему тексту.
    if (value.length === 0 || value === task.title) return;
    await patch(task, { title: value });
  }

  async function setNotes(task: Task, notes: string): Promise<void> {
    // Описание пустым быть вправе: это и есть «поле не заполнено» (3.5).
    const value = notes.length === 0 ? null : notes;
    if (value === task.notes) return;
    await patch(task, { notes: value });
  }

  async function setDue(task: Task, due: string | null, dueTime: string | null): Promise<void> {
    // Время без даты бессмысленно, и снятие даты уносит время той же мутацией:
    // порознь два устройства успели бы разъехаться, и время осталось бы висеть
    // в записи без даты (этап 21 плана). Читать его никто не станет, но и
    // копить мусор незачем.
    const time = due === null ? null : dueTime;
    if (due === task.due && time === task.due_time) return;
    await patch(task, { due, due_time: time });
  }

  async function setRepeat(task: Task, repeat: Repeat | null): Promise<void> {
    const fields = repeatChange(task, repeat);
    if (Object.keys(fields).length === 0) return;
    await patch(task, fields);
  }

  async function setSprint(task: Task, sprint: SprintKey | null): Promise<void> {
    await moveToSprint(task, sprint);
  }

  async function remove(task: Task): Promise<void> {
    // Надгробие, а не удаление строки: устройство, бывшее оффлайн, иначе не
    // отличит «задачу удалили» от «эту задачу я ещё не получил».
    await engine.mutate({ entity: 'task', entityId: task.id, kind: 'delete' });
  }

  async function restore(task: Task): Promise<void> {
    // Особого поля «воскресить» нет и не нужно: `applyMutation` в `shared`
    // проставляет любому upsert `deleted_at: null` со своими часами, то есть
    // более поздняя правка сама снимает надгробие. Тем же приёмом возвращают
    // снятую связь.
    //
    // Полей не передаём намеренно: правка обязана снять надгробие, но не
    // трогать остальное. Задачу могли править с другого устройства уже после
    // удаления — вернуть её вместе со своим снимком значило бы затереть те
    // правки чужими часами, и человек получил бы не ту задачу, что удалял.
    await engine.mutate({ entity: 'task', entityId: task.id, kind: 'upsert' });
  }

  async function purge(task: Task): Promise<void> {
    // Не повторное `delete`: оно обновило бы время удаления, то есть продлило
    // бы задаче жизнь ровно там, где её просят прекратить. `purge` сдвигает
    // время за границу срока — задача пропадает из корзины на всех
    // устройствах обычным слиянием, а строку снимет уборка.
    await engine.mutate({ entity: 'task', entityId: task.id, kind: 'purge' });
  }

  async function setSprintType(userId: string, type: SprintType): Promise<void> {
    // У настроек одна строка на пользователя, и ключ у неё — сам пользователь.
    // `entity_id` всё равно обязан быть uuid (схема мутации), поэтому туда
    // идёт тот же идентификатор; сервер его игнорирует, а `seed` нужен
    // локальной копии, чтобы строка легла под правильным ключом.
    await engine.mutate({
      entity: 'settings',
      entityId: userId,
      kind: 'upsert',
      fields: { sprint_type: type },
      seed: { user_id: userId },
    });
  }

  // ------------------------------------------------------------------------
  // Проекты и связи
  // ------------------------------------------------------------------------

  async function setParent(
    task: Task,
    parentId: string | null,
    tasks: readonly Task[],
  ): Promise<ParentOutcome> {
    if (task.parent_id === parentId) return 'same';

    // Вынуть задачу из проекта можно всегда: это снятие ограничения, а не
    // добавление уровня.
    if (parentId !== null) {
      // Уровней ровно два (`canBeProject` / `canHaveParent` в `shared`):
      // проект не бывает вложенным, а вложенная задача не бывает проектом.
      const parent = tasks.find((item) => item.id === parentId);
      if (parent !== undefined && !canBeProject(parent)) return 'nested-parent';
      if (!canHaveParent(task.id, tasks)) return 'is-project';

      // Цепочку замыкать нельзя (3.8.3). При двух уровнях это уже невозможно,
      // но проверка страхует данные, заведённые до правила.
      if (wouldCycle(task.id, parentId, new Map(tasks.map((item) => [item.id, item.parent_id])))) {
        return 'cycle';
      }
    }

    await patch(task, { parent_id: parentId });
    return 'set';
  }

  async function createProject(
    title: string,
    sprint: SprintKey | null,
    afterOrderKey: string | null,
    parentId: string | null = null,
  ): Promise<{ outcome: CreateOutcome; id?: string }> {
    // Проект — обычная задача (3.8.1), поэтому заводится тем же кодом. Всё
    // отличие в том, что сразу после неё вызывающий вкладывает в неё текущую.
    //
    // `parentId` нужен подзадаче: вложить её надо в момент создания, потому
    // что снаружи есть только идентификатор новой задачи, а `setParent` берёт
    // саму запись — она появится в локальной копии лишь к следующей отрисовке.
    return createTask(title, sprint, afterOrderKey, parentId);
  }

  async function addLink(
    blockerId: string,
    blockedId: string,
    known: readonly TaskLink[],
  ): Promise<LinkOutcome> {
    if (blockerId === blockedId) return 'same-task';

    // `known` — все записи связей, **включая надгробия**, и это принципиально.
    const same = known.find(
      (link) => link.blocker_id === blockerId && link.blocked_id === blockedId,
    );
    if (same !== undefined && same.deleted_at == null) return 'duplicate';

    // Пара «кто кого держит» уникальна в базе (`task_links_pair_uniq` в
    // 0001_init.sql), и индекс **не частичный**: он покрывает и снятые связи.
    // Значит снятую связь нельзя завести заново новой записью — сервер
    // отвергнет её как дубль, правка уедет в карантин, а на экране останется
    // «правки не приняты сервером» без объяснения. Поэтому у пары одна запись
    // на всю жизнь: снятие ставит ей надгробие, возврат — воскрешает.
    //
    // Воскрешать особым полем не нужно: `applyMutation` в `shared` любому
    // upsert проставляет `deleted_at: null` со своими часами, то есть более
    // поздняя правка сама снимает надгробие.
    //
    // `created_at` проставляется здесь, а не умолчанием базы: поле, которое
    // выставил сервер, до устройства-автора не доезжает вовсе — слияние
    // копирует только поля с часами в `field_clocks`.
    await engine.mutate({
      entity: 'task_link',
      entityId: same?.id ?? crypto.randomUUID(),
      kind: 'upsert',
      fields: {
        blocker_id: blockerId,
        blocked_id: blockedId,
        created_at: now().toISOString(),
      },
    });
    return 'added';
  }

  async function removeLink(link: TaskLink): Promise<void> {
    // Снять связь можно с любой из сторон (3.9.3), но запись у неё одна:
    // надгробие ставится ей, кто бы ни нажал.
    await engine.mutate({ entity: 'task_link', entityId: link.id, kind: 'delete' });
  }

  // ------------------------------------------------------------------------
  // Теги
  // ------------------------------------------------------------------------

  async function addTag(
    taskId: string,
    raw: string,
    known: readonly TaskTag[],
  ): Promise<TagOutcome> {
    // Нормализация та же, что и у разбора быстрой записи и у слепого индекса:
    // «Дом» и «дом» обязаны быть одним тегом во всех трёх местах.
    const tag = normalizeTag(raw.replace(/^#/, ''));
    if (tag.length === 0) return 'empty';

    // `known` — все записи тегов задачи, **включая снятые**, и это
    // принципиально. Пара «задача и тег» уникальна в базе вместе с
    // надгробиями (`task_tags_task_bidx_uniq`, индекс не частичный), поэтому
    // снятый тег нельзя вернуть новой записью: сервер отвергнет её как дубль,
    // правка уедет в карантин, а тег на экране просто исчезнет. Ровно это и
    // случилось на проде — та же ловушка, что уже была со связями.
    //
    // Поэтому у пары одна запись на всю жизнь: снятие ставит ей надгробие,
    // возврат — воскрешает. Особого поля для этого не нужно: `applyMutation`
    // любому upsert проставляет `deleted_at: null` со своими часами.
    const same = known.find((record) => record.tag === tag);
    if (same !== undefined && same.deleted_at == null) return 'duplicate';

    await engine.mutate({
      entity: 'task_tag',
      entityId: same?.id ?? crypto.randomUUID(),
      kind: 'upsert',
      fields: { task_id: taskId, tag, created_at: now().toISOString() },
    });
    return 'added';
  }

  async function removeTag(tag: TaskTag): Promise<void> {
    await engine.mutate({ entity: 'task_tag', entityId: tag.id, kind: 'delete' });
  }

  // ------------------------------------------------------------------------
  // Чек-лист
  // ------------------------------------------------------------------------

  async function addChecklistItem(
    taskId: string,
    text: string,
    afterOrderKey: string | null,
  ): Promise<boolean> {
    const value = text.trim();
    if (value.length === 0) return false;

    await engine.mutate({
      entity: 'checklist_item',
      entityId: crypto.randomUUID(),
      kind: 'upsert',
      fields: {
        task_id: taskId,
        text: value,
        done: false,
        order_key: orderKeyBetween(afterOrderKey, null),
        created_at: now().toISOString(),
      },
    });
    return true;
  }

  async function setChecklistText(item: ChecklistItem, text: string): Promise<void> {
    const value = text.trim();
    if (value.length === 0 || value === item.text) return;
    await engine.mutate({
      entity: 'checklist_item',
      entityId: item.id,
      kind: 'upsert',
      fields: { text: value },
    });
  }

  async function toggleChecklistItem(item: ChecklistItem): Promise<void> {
    // Статус задачи чек-лист не трогает: закрыть задачу можно и с
    // невыполненными пунктами (3.7.4).
    await engine.mutate({
      entity: 'checklist_item',
      entityId: item.id,
      kind: 'upsert',
      fields: { done: !item.done },
    });
  }

  async function removeChecklistItem(item: ChecklistItem): Promise<void> {
    await engine.mutate({ entity: 'checklist_item', entityId: item.id, kind: 'delete' });
  }

  return {
    create,
    setStatus,
    reopen,
    shiftStatus,
    place,
    shiftTaskSprint,
    carryOver,
    setTitle,
    setNotes,
    setDue,
    setSprint,
    setRepeat,
    remove,
    restore,
    purge,
    setSprintType,
    setParent,
    createProject,
    addLink,
    removeLink,
    addTag,
    removeTag,
    addChecklistItem,
    setChecklistText,
    toggleChecklistItem,
    removeChecklistItem,
  };
}
